/**
 * Utilitários para gerenciamento de planos
 * Substitui a lógica de account_type pelo novo sistema de plans
 */

// Constantes dos planos
export const PLANS = {
  CLIENT: "1",
  FOUNDER: "2",
  STARTER: "3",              // OnDemand legado (comercial)
  GROWTH: "4",               // PRO legado
  SCALE: "5",                // Business legado (whitelabel)
  FREE: "6",                 // Gratuito legado (pessoal)
  PERSONAL_STARTER: "7",     // Starter Pessoal
  PERSONAL_BUSINESS: "8",    // Business Pessoal
  WHITE_LABEL: "9",          // White Label
  WL_PLUS: "10",             // White Label Plus
  WL_PRO: "11",              // White Label Pro
};

// Labels dos planos
export const PLAN_LABELS = {
  "1": "Client",
  "2": "Founder",
  "3": "On Demand",
  "4": "PRO",
  "5": "Business",
  "6": "Gratuito",
  "7": "Starter",
  "8": "Business",
  "9": "White Label",
  "10": "White Label Plus",
  "11": "White Label Pro",
};

// Grupos funcionais
export const CLIENT_PLAN = ["1"];
export const FOUNDER_PLAN = ["2"];
export const STARTER_PLAN = ["3"];
export const PRO_PLAN = ["4"];
export const SCALE_PLAN = ["5"];
export const FREE_PLAN = ["6"];
export const PERSONAL_STARTER_PLAN = ["7"];
export const PERSONAL_BUSINESS_PLAN = ["8"];
export const WHITE_LABEL_PLAN = ["9"];
export const WL_PLUS_PLAN = ["10"];
export const WL_PRO_PLAN = ["11"];

// Planos de uso pessoal (sem subcontas/organização)
export const PERSONAL_PLANS = ["6", "7", "8"];

// Planos comerciais (com subcontas/organização)
export const COMMERCIAL_PLANS = ["3", "5", "9", "10", "11"];

// Planos com acesso a white label / organização
export const WHITELABEL_PLANS = ["2", "5", "9", "10", "11"];

// Planos avançados (funcionalidades completas: API, email, integrações, etc.)
export const ADVANCED_PLANS = ["2", "3", "4", "5", "8", "9", "10", "11"];

// Todos os planos válidos (exclui apenas Client=1 que é interno para subcontas)
export const ALL_PLANS = ["2", "3", "4", "5", "6", "7", "8", "9", "10", "11"];

// Funções de verificação de categoria
export const isClientPlan = (plan) => {
  return CLIENT_PLAN.includes(String(plan));
};

export const isFounderPlan = (plan) => {
  return FOUNDER_PLAN.includes(String(plan));
};

export const isStarterPlan = (plan) => {
  return STARTER_PLAN.includes(String(plan));
};

export const isProPlan = (plan) => {
  return PRO_PLAN.includes(String(plan));
};

export const isScalePlan = (plan) => {
  return SCALE_PLAN.includes(String(plan));
};

export const isFreePlan = (plan) => {
  return FREE_PLAN.includes(String(plan));
};

export const isPersonalStarterPlan = (plan) => {
  return PERSONAL_STARTER_PLAN.includes(String(plan));
};

export const isPersonalBusinessPlan = (plan) => {
  return PERSONAL_BUSINESS_PLAN.includes(String(plan));
};

export const isWhiteLabelPlan = (plan) => {
  return WHITE_LABEL_PLAN.includes(String(plan));
};

export const isWlPlusPlan = (plan) => {
  return WL_PLUS_PLAN.includes(String(plan));
};

export const isWlProPlan = (plan) => {
  return WL_PRO_PLAN.includes(String(plan));
};

export const isPersonalPlan = (plan) => {
  return PERSONAL_PLANS.includes(String(plan));
};

export const isCommercialPlan = (plan) => {
  return COMMERCIAL_PLANS.includes(String(plan));
};

export const isWhitelabelEnabled = (plan) => {
  return WHITELABEL_PLANS.includes(String(plan));
};

export const isAdvancedPlan = (plan) => {
  return ADVANCED_PLANS.includes(String(plan));
};

export const isAllPlan = (plan) => {
  return ALL_PLANS.includes(String(plan));
};

// Função para obter categoria do plano
export const getPlanCategory = (plan) => {
  const planStr = String(plan);
  if (planStr === "1") return "client";
  if (planStr === "2") return "founder";
  if (planStr === "3") return "starter";
  if (planStr === "4") return "pro";
  if (planStr === "5") return "scale";
  if (planStr === "6") return "free";
  if (planStr === "7") return "personal-starter";
  if (planStr === "8") return "personal-business";
  if (planStr === "9") return "white-label";
  if (planStr === "10") return "wl-plus";
  if (planStr === "11") return "wl-pro";
  return "client"; // fallback
};

// Função para obter label do plano
export const getPlanLabel = (plan) => {
  return PLAN_LABELS[String(plan)] || "Client";
};

// Função para converter account_type para plan (compatibilidade)
export const accountTypeToPlan = (accountType) => {
  const mapping = {
    1: "1", // Client
    2: "2", // Founder (ex-Creator)
    3: "3"  // Starter (ex-Agency, usa o menor plano agency)
  };
  return mapping[accountType] || "1";
};

// Função para converter plan para account_type (compatibilidade reversa)
export const planToAccountType = (plan) => {
  if (isClientPlan(plan)) return 1;
  if (isAdvancedPlan(plan)) return 3;
  if (isStarterPlan(plan)) return 1;
  return 1;
};

// Função para verificar se o plano tem acesso a funcionalidades específicas
export const hasFeatureAccess = (plan, feature) => {
  const featureAccess = {
    whatsapp_connection: (plan) => isAdvancedPlan(plan),
    dashboard_agency: (plan) => isAdvancedPlan(plan),
    organization_management: (plan) => isWhitelabelEnabled(plan),
    support_configuration: (plan) => isAdvancedPlan(plan),
    support_request: (plan) => isClientPlan(plan),
    training_access: (plan) => true,
    training_management: (plan) => isAdvancedPlan(plan),
    automation_templates: (plan) => isAdvancedPlan(plan),
    crm_access: (plan) => isAdvancedPlan(plan),
    calendar_access: (plan) => isAdvancedPlan(plan)
  };

  return featureAccess[feature] ? featureAccess[feature](plan) : false;
};

// Função para obter menu baseado no plano
export const getMenuType = (plan) => {
  return getPlanCategory(plan);
};

// Função para validar se o plano é válido
export const isValidPlan = (plan) => {
  return Object.values(PLANS).includes(String(plan));
};

// Função para obter plano padrão
export const getDefaultPlan = () => {
  return PLANS.CLIENT;
};
